import type { ManifestRecord } from "./driver-api.ts";
import type { ChipClass } from "./shift-readiness.ts";

/**
 * `Handoff` — the manifest a courier is collecting at a hub, and what they can check against it.
 *
 * The design's screen, value for value from `15 WP14 Driver Onboarding and Earnings.dc.html`:
 *
 *     handoff:{l:'Handoff',art:'scan',on:1,chip:['active','line-haul → last mile'],
 *       title:'Collect 14 parcels from Hub DAR-01',
 *       sub:'Manifest man_88K2 · bay 4',
 *       rows:[['Scanned','11 of 14'],['Missing','3 · keep scanning'],['Seal','SL-2291 intact']],
 *       note:['pending','You take custody only when every parcel is scanned or marked missing. The hub signs off with you.'],
 *       btns:[['Scan next parcel','qr_code_scanner',1],['Report missing','report',0]]}
 *
 * ## The screen was mapped to the wrong service, and that is worth recording
 *
 * `navigation.ts` recorded this as waiting on *a public handoff read; only
 * `GET /internal/v1/handoffs/{handoffId}` exists*. That read is LOG's **last-mile handoff**, which
 * is the saga that turns a shipment arriving at its destination hub into a delivery order — it holds
 * a shipment, a hub, a state and a delivery order identifier, and it has no seal, no bay and no
 * parcel in it. It was never going to answer this screen however public it became.
 *
 * What the design draws is a **transport manifest**: a sealed collection at a hub, with a seal
 * number on the door and a declared count inside. LOG-07 owns that, and had published both a listing
 * and a by-identifier read, publicly, all along. What it did not record was what sealing produces —
 * ADR 0077 adds `sealId`, `bay` and `parcelCount`, set by the seal command.
 *
 * ## One of the three rows is still an absence, and it is the one the screen is named for
 *
 * `Scanned 11 of 14` and `Missing 3` are a courier's progress against this manifest, and **nothing
 * publishes it**. LOG-06 stores scans keyed by shipment and parcel with no manifest on them, and
 * publishes one internal read by scan identifier — no listing at any tenancy bound. A manifest lists
 * shipments, so "scanned for this manifest" is derivable in principle and by **neither service
 * alone**: LOG-06 does not know what a manifest is, and LOG-07 does not see scans.
 *
 * That is a read model somebody has to decide to build, not a field somebody forgot. The board draws
 * the two rows as absences naming it, and the courier still gets the three things they came for —
 * what to collect, how many, and the seal to check. Q-226.
 */

/** The design's own note, word for word. */
export const CUSTODY_NOTE =
  "You take custody only when every parcel is scanned or marked missing. The hub signs off with you.";

/** Nothing joins a scan to a manifest, so neither count can be drawn. */
export const NO_SCAN_PROGRESS =
  "Nothing publishes a courier's scan progress against a manifest: LOG-06 keys a scan to a shipment and a parcel with no manifest on it and publishes no listing, and LOG-07 does not see scans";

/**
 * `Collect 14 parcels from Hub DAR-01`, with the count the hub declared.
 *
 * The hub is the **origin**, which is where a courier collects. `destinationHubId` is where the
 * manifest is going, and drawing it here would send somebody to the wrong end of a line haul.
 *
 * An unsealed manifest has no declared count, so the title says what it can rather than guessing
 * one: `shipmentIds.length` is a count of shipments and this cell is a count of parcels, and the two
 * are only equal when every shipment holds exactly one.
 */
export function handoffTitle(manifest: ManifestRecord): {
  readonly text: string;
  readonly designed: boolean;
} {
  if (manifest.parcelCount === null) {
    return {
      text: `Collect from Hub ${manifest.originHubId}`,
      designed: false,
    };
  }
  const parcels = `${String(manifest.parcelCount)} ${manifest.parcelCount === 1 ? "parcel" : "parcels"}`;
  return { text: `Collect ${parcels} from Hub ${manifest.originHubId}`, designed: true };
}

/** `Manifest man_88K2 · bay 4`, losing the bay where the hub does not number them. */
export function handoffSub(manifest: ManifestRecord): string {
  return manifest.bay === null
    ? `Manifest ${manifest.manifestId}`
    : `Manifest ${manifest.manifestId} · bay ${manifest.bay}`;
}

/**
 * The chip. The design draws `['active','line-haul → last mile']`, which is the leg rather than a
 * state, so the leg is drawn from the two hubs the manifest actually names and the class comes from
 * its status — a cancelled manifest shown `active` would send a courier to collect a truck that is
 * not going.
 */
export function handoffChip(manifest: ManifestRecord): {
  readonly class: ChipClass;
  readonly label: string;
} {
  const leg = `${manifest.originHubId} → ${manifest.destinationHubId}`;
  switch (manifest.status) {
    case "sealed":
      return { class: "active", label: leg };
    case "handed_off":
      return { class: "done", label: `${leg} · handed off` };
    case "cancelled":
      return { class: "closed", label: `${leg} · cancelled` };
    default:
      // `open`: still being loaded, so there is nothing sealed to collect yet.
      return { class: "waiting", label: `${leg} · still loading` };
  }
}

/** One key/value row, where `value: null` is an absence the shell draws as one. */
export interface HandoffRow {
  readonly key: string;
  readonly value: string | null;
  readonly because?: string;
}

/**
 * The design's three rows, two of which have no source.
 *
 * `Seal` is the one that is fully drawn and it is the one a courier acts on: the number on the row
 * against the number on the door. The design writes `SL-2291 intact`, and **`intact` is not drawn**
 * — nothing on this platform records whether a seal was found broken, and a screen asserting a seal
 * is intact is the one thing it must not do when the courier is standing in front of it.
 */
export function handoffRows(manifest: ManifestRecord): HandoffRow[] {
  return [
    { key: "Scanned", value: null, because: NO_SCAN_PROGRESS },
    { key: "Missing", value: null, because: NO_SCAN_PROGRESS },
    {
      key: "Seal",
      value: manifest.sealId,
      ...(manifest.sealId === null
        ? {
            because:
              "This manifest has not been sealed, so there is no seal number to check against the door",
          }
        : {}),
    },
  ];
}
